import { Component, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { RouterLink, Router } from '@angular/router';

import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ButtonModule } from 'primeng/button';
import { OverlayPanelModule, OverlayPanel } from 'primeng/overlaypanel';
import { BadgeModule } from 'primeng/badge';

import { UserProfileService } from '@core/services/user.service';

import { OnboardingService } from '../../../../onboarding/shared/services/onboarding.service';

@Component({
  selector: 'app-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.scss',
  imports: [
    ScrollPanelModule,
    ScrollPanelModule,
    ButtonModule,
    OverlayPanelModule,
    NgIf,
    NgForOf,
    DatePipe,
    BadgeModule,
    RouterLink,
  ],
})
export class NotificationPanelComponent implements AfterViewInit, OnDestroy {
  notifications: any[] = [];
  private subscription: Subscription = new Subscription();
  unreadCount = 0;
  userData: any;
  private userDataSubscription?: Subscription;

  constructor(
    private onboardingService: OnboardingService,
    private userService: UserProfileService,
    private router: Router
  ) {
    this.fetchNotifications();
    this.getUserData();
  }

  getUserData() {
    this.userDataSubscription = this.userService.getUser().subscribe((res) => {
      if (res && res.user) {
        this.userData = res;
      }
    });
  }

  fetchNotifications(): void {
    const notificationsObservable = this.onboardingService.getUserNotifications();
    if (!notificationsObservable) {
      this.notifications = [];
      this.updateUnreadCount();
      return;
    }
    const sub = notificationsObservable.subscribe((snapshot) => {
      this.notifications = snapshot.sort(
        (a: any, b: any) => b.created_at.toDate() - a.created_at.toDate()
      );
      this.updateUnreadCount();
      this.updateUnreadCount();
    });
    this.subscription.add(sub);
  }

  // In your component
  async markAllAsRead() {
    if (!this.notifications.length) return;
    const originalNotifications = [...this.notifications];
    this.notifications = [];
    this.unreadCount = 0;
    try {
      const allIds = originalNotifications.map((n) => n.id).filter((id): id is string => !!id);
      if (allIds.length > 0) {
        await this.onboardingService.deleteAllNotifications(allIds);
      }
    } catch (error) {
      this.notifications = originalNotifications;
      this.updateUnreadCount();
      console.error('Failed to delete all notifications:', error);
    }
  }

  private updateUnreadCount(): void {
    this.unreadCount = this.notifications.filter((n) => !n.is_read).length;
  }

  // Method to handle navigation with refresh for same route
  navigateToPost(postId: string) {
    const currentUrl = this.router.url;
    const targetUrl = `/create/${postId}`;

    if (currentUrl === targetUrl) {
      // If already on the same route, navigate away and back to trigger refresh
      this.router.navigateByUrl('/planner/list', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(targetUrl);
      });
    } else {
      // Normal navigation for different routes
      this.router.navigateByUrl(targetUrl);
    }
  }

  // Method to handle navigation to planner list with refresh
  navigateToPlanner(selectedTab: string) {
    const currentUrl = this.router.url;
    const targetUrl = `/planner/list?selectedTab=${selectedTab}`;

    if (currentUrl.includes('/planner/list')) {
      // If already on the same route, navigate away and back to trigger refresh
      this.router.navigateByUrl('/create', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(targetUrl);
      });
    } else {
      // Normal navigation for different routes
      this.router.navigateByUrl(targetUrl);
    }
  }
  getNotificationIcon(type: string): string {
    const icons: Record<string, string> = {
      post_activity: 'assets/images/user-setting/notification.svg',
      connection_error: 'assets/images/html/connection-error.svg',
      post_status: 'assets/images/user-setting/notification.svg',
      COMMENT: 'assets/images/html/comment-notification.svg',
      needs_approval: 'assets/images/html/post-approval.svg',
      published: 'assets/images/html/post-published.svg',
      failed: 'assets/images/html/post-failed.svg',
      scheduled: 'assets/images/html/post-scheduled.svg',
    };
    return icons[type] || 'assets/images/user-setting/notification.svg';
  }

  getNotificationText(notification: any): string {
    const { type, custom_data } = notification;
    let workspaceName = '';

    if (this.userData?.workspaces && custom_data?.workspace_id) {
      const workspace = this.userData.workspaces.find(
        (w: any) => w.uuid === custom_data.workspace_id
      );
      workspaceName = workspace ? ` in workspace "${workspace.name}"` : '';
    }
    switch (type) {
      case 'post_activity':
        return `You have a new comment${workspaceName}: "${this.stripHtml(custom_data.text)}"`;
      case 'connection_error':
        return `Connection error for ${custom_data.provider} (${custom_data.name})${workspaceName}.`;
      default:
        if (custom_data?.type === 'failed') {
          return `A post has failed${workspaceName}.`;
        }
        if (custom_data?.type === 'needs_approval') {
          return `A post requires your approval${workspaceName}.`;
        }
        return `You have a new notification${workspaceName}.`;
    }
  }

  private stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  @ViewChild('notify') overlayPanel: OverlayPanel | undefined;

  ngAfterViewInit() {
    window.addEventListener('scroll', this.hideOverlayOnScroll, true);
  }

  hideOverlayOnScroll = (event: Event) => {
    const target = event.target as HTMLElement;

    if (!this.overlayPanel || !this.overlayPanel.container) return;

    const overlayElement = this.overlayPanel.container;

    // Check if the scroll event started inside the overlay panel
    if (overlayElement.contains(target)) {
      return; // don't hide if scrolling inside
    }

    this.overlayPanel.hide();
  };

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.hideOverlayOnScroll, true);

    this.subscription.unsubscribe();
    this.userDataSubscription?.unsubscribe();
  }
}
